import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mobileAdminQueryKeys } from '../query';
import { deviceAdminApi } from './api';
import type { ApiResponse } from '../types';
import type { BootstrapData, SafeDevice } from './types';

export const deviceKeys = {
    current: (studentId: string) => [...mobileAdminQueryKeys.devices(), 'current', studentId] as const,
    requests: (status: string, page: number, limit: number) => [...mobileAdminQueryKeys.deviceChanges(), { status, page, limit }] as const,
};

export function useCurrentDevice(studentId: string, enabled: boolean) {
    return useQuery({ queryKey: deviceKeys.current(studentId), queryFn: () => deviceAdminApi.current(studentId), enabled: enabled && Boolean(studentId), retry: false });
}

export function useDeviceAction(studentId: string) {
    const client = useQueryClient();
    return useMutation<ApiResponse<{ device: SafeDevice } | BootstrapData>, Error, { action: 'revoke' | 'bootstrap'; reason?: string }>({
        mutationFn: async ({ action, reason }) => action === 'revoke' ? deviceAdminApi.revoke(studentId, reason || '') : deviceAdminApi.bootstrap(studentId),
        onSuccess: () => client.invalidateQueries({ queryKey: deviceKeys.current(studentId) }),
    });
}

export function useDeviceChangeRequests(status: string, page: number, limit = 20) {
    return useQuery({ queryKey: deviceKeys.requests(status, page, limit), queryFn: () => deviceAdminApi.list({ status, page, limit }), retry: false });
}

export function useDeviceChangeActivity(enabled: boolean) {
    return useQuery({ queryKey: [...mobileAdminQueryKeys.deviceChanges(), 'activity', 30], queryFn: () => deviceAdminApi.activity(30, 30), enabled, retry: false, refetchInterval: enabled ? 30000 : false });
}

export function useReviewDeviceChange() {
    const client = useQueryClient();
    return useMutation({
        mutationFn: ({ requestId, decision, reviewNote }: { requestId: string; decision: 'approve' | 'reject'; reviewNote: string }) => decision === 'approve' ? deviceAdminApi.approve(requestId, reviewNote) : deviceAdminApi.reject(requestId, reviewNote),
        onSuccess: () => client.invalidateQueries({ queryKey: mobileAdminQueryKeys.deviceChanges() }),
    });
}
